import type { TileContentLease, TileContentPackage } from '@/plugins/tiles/contracts'
import { tileCostBytes, tileMemoryCosts } from '@/plugins/tiles/runtime/TileCache'
import { TileRetirementQueue } from '@/plugins/tiles/runtime/TileRetirementQueue'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('tiles:registry')
interface Entry {
  readonly content: TileContentPackage
  references: number
  pendingDispose: boolean
}

/** 包唯一所有者；关闭禁止新租约，但旧租约仍可退出并最终清理。 */
export class TileContentRegistry {
  private _entries = new Map<string, Entry>()
  private _owners = new WeakMap<TileContentPackage, string>()
  private _disposed = new WeakSet<TileContentPackage>()
  private _closed = false
  readonly retirement?: TileRetirementQueue
  private readonly _listeners = new Set<() => void>()

  constructor(
    private readonly _onReleaseError?: (error: unknown) => void,
    deferred = false,
    onChange?: () => void
  ) {
    if (onChange) this._listeners.add(onChange)
    if (deferred)
      this.retirement = new TileRetirementQueue(
        (error) => this.report(error),
        () => this.notify()
      )
  }

  onChange(listener: () => void): () => void {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }
  private notify(): void {
    for (const listener of this._listeners) {
      try {
        listener()
      } catch {
        /* 清理继续。 */
      }
    }
  }

  getDeferredCosts() {
    const total = {
      ...(this.retirement?.getStatus() ?? { count: 0, bytes: 0, cpuBytes: 0, gpuBytes: 0 }),
    }
    for (const entry of this._entries.values()) {
      if (!entry.pendingDispose) continue
      const costs = entry.content.costs
      const memory = tileMemoryCosts(costs)
      total.count++
      total.bytes += tileCostBytes(costs)
      total.cpuBytes += memory.cpuBytes
      total.gpuBytes += memory.gpuBytes
    }
    return total
  }

  get size(): number {
    return this._entries.size
  }

  /** 始终接管未被本注册表拥有的输入；关闭或 ID 冲突时立即释放新输入。 */
  register(id: string, content: TileContentPackage): boolean {
    if (this._disposed.has(content)) return false
    const owner = this._owners.get(content)
    if (owner !== undefined)
      return !this._closed && owner === id && !this._entries.get(id)?.pendingDispose
    if (this._closed || this._entries.has(id)) {
      this._dispose(content)
      return false
    }
    this._owners.set(content, id)
    this._entries.set(id, { content, references: 0, pendingDispose: false })
    return true
  }

  get(id: string): TileContentPackage | undefined {
    return this._entries.get(id)?.content
  }
  has(id: string): boolean {
    return this._entries.has(id)
  }
  refCount(id: string): number {
    return this._entries.get(id)?.references ?? 0
  }

  retain(id: string): TileContentLease | undefined {
    const entry = this._entries.get(id)
    if (this._closed || !entry || entry.pendingDispose) return undefined
    entry.references++
    let released = false
    return {
      content: entry.content,
      release: () => {
        if (released) return
        released = true
        entry.references--
        this._finalize(id, entry)
      },
    }
  }

  requestDispose(id: string): void {
    const entry = this._entries.get(id)
    if (!entry) return
    entry.pendingDispose = true
    this._finalize(id, entry)
  }

  close(): void {
    this._closed = true
    for (const id of this._entries.keys()) this.requestDispose(id)
    this.retirement?.close()
    this.notify()
  }

  /** 无注册 ID 的迟到/拒收结果也经此去重清理，不破坏已有包的租约。 */
  discard(content: TileContentPackage): void {
    const owner = this._owners.get(content)
    if (owner !== undefined && this._entries.get(owner)?.content === content) return
    this._dispose(content)
  }

  private _finalize(id: string, entry: Entry): void {
    if (!entry.pendingDispose || entry.references) return
    this._entries.delete(id)
    this._dispose(entry.content)
    this.notify()
  }

  private _dispose(content: TileContentPackage): void {
    if (this._disposed.has(content)) return
    this._disposed.add(content)
    try {
      if (this.retirement) this.retirement.enqueue(content)
      else content.dispose()
    } catch (error) {
      this.report(error)
    }
  }

  private report(error: unknown): void {
    log.error(`内容资源释放失败: ${String(error)}`)
    try {
      this._onReleaseError?.(error)
    } catch {
      /* 单项诊断异常不能阻止其他包清理。 */
    }
  }
}
